/**
 * LumaScript Preview Panel
 *
 * Automatically visualizes LumaScript JSON as a flow diagram using ReactFlow.
 * Shows the workflow structure immediately when LumaScript is generated.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import ReactFlow, {
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  ReactFlowProvider,
  useReactFlow,
  type NodeTypes,
  type Node,
  type Edge
} from 'reactflow';
import 'reactflow/dist/style.css';

import { useProjectStore } from '@/stores/projectStore';
import { useToastStore } from '@/stores/toastStore';
import { Button } from '../ui/Button';
import { ArrowLeft, Loader2, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { transformApi, readStream } from '@/lib/api';
import { CustomNode } from '../canvas/nodes/CustomNode';
import { LumaSkillTransformers, TransformError } from '@/utils/lumaSkillTransformers';
import { normalizeArtifactDesign } from '@/utils/artifactDesign';
import { ConfirmDialog } from '../ui/ConfirmDialog';

// Register custom node type
const nodeTypes: NodeTypes = {
  custom: CustomNode
};

const LumaScriptPreviewContent: React.FC = () => {
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const selectedArtifactId = useProjectStore((state) => state.ui.selectedArtifactId);
  const projects = useProjectStore((state) => state.projects);
  const updateProjectStage = useProjectStore((state) => state.updateProjectStage);
  const setActiveStage = useProjectStore((state) => state.setActiveStage);
  const { addToast } = useToastStore();
  const { fitView } = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get current project and artifact
  const project = activeProjectId ? projects[activeProjectId] : null;
  const artifact = project && selectedArtifactId ? project.artifacts[selectedArtifactId] : null;

  // Context detection
  const isProjectContext = !selectedArtifactId;

  // Get LumaScript content based on context (project or artifact level)
  const lumascript = isProjectContext
    ? project?.stages.LUMASCRIPT?.content || null
    : artifact?.stages?.LUMASCRIPT?.content || null;

  // ResizeObserver to detect when container gets dimensions
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0 && !isReady) {
          // Container now has dimensions, mark as ready
          setIsReady(true);
          // Trigger fitView after a short delay to ensure ReactFlow is fully initialized
          setTimeout(() => {
            try {
              fitView({ padding: 0.2, duration: 200 });
            } catch (e) {
              // fitView not ready yet
            }
          }, 100);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isReady, fitView]);

  // Parse and visualize LumaScript whenever it changes
  useEffect(() => {
    if (!lumascript) {
      setNodes([]);
      setEdges([]);
      setParseError(null);
      return;
    }

    try {
      // Parse JSON
      const lumaScriptObj = JSON.parse(lumascript);

      // Transform OOTB format to canonical format
      const skill = LumaSkillTransformers.fromOOTBFormat(lumaScriptObj);

      // Apply auto-layout
      const layoutedSkill = LumaSkillTransformers.autoLayout(skill, {
        direction: 'TB',
        nodeWidth: 200,
        nodeHeight: 80,
        rankSep: 100,
        nodeSep: 50
      });

      // Convert to ReactFlow format
      const { nodes: flowNodes, edges: flowEdges } = LumaSkillTransformers.toReactFlow(layoutedSkill);

      setNodes(flowNodes);
      setEdges(flowEdges);
      setParseError(null);

      // Trigger fitView when nodes are ready and container is sized
      if (isReady && flowNodes.length > 0) {
        setTimeout(() => {
          try {
            fitView({ padding: 0.2, duration: 200 });
          } catch (e) {
            // fitView not ready yet
          }
        }, 100);
      }
    } catch (error: any) {
      console.error('[LumaScriptPreview] Failed to parse LumaScript:', error);
      setParseError(error.message || 'Failed to parse LumaScript');
      setNodes([]);
      setEdges([]);
    }
  }, [lumascript, setNodes, setEdges, isReady, fitView]);

  const handleToBPMNPreview = () => {
    if (!lumascript) return;
    // Force tab switch even if already on BPMN stage
    // This handles the case where user manually clicked away from BPMN tab via GoldenLayout UI
    const currentStage = useProjectStore.getState().ui.activeStage;
    if (currentStage === 'BPMN') {
      // Temporarily switch to a different stage, then back to BPMN to force the effect to run
      setActiveStage('LUMASCRIPT');
      setTimeout(() => setActiveStage('BPMN'), 0);
    } else {
      setActiveStage('BPMN');
    }
  };

  const handleToArtifactDesignClick = () => {
    if (!lumascript) return;

    // Check if ARTIFACT_DESIGN stage already has content
    const artifactDesignStage = project?.stages.ARTIFACT_DESIGN;
    if (artifactDesignStage && artifactDesignStage.content.trim()) {
      setShowConfirm(true);
    } else {
      executeToArtifactDesign();
    }
  };

  const executeToArtifactDesign = async () => {
    if (!activeProjectId || !project || !lumascript) return;

    setAnalyzing(true);
    try {
      const lumascriptObj = JSON.parse(lumascript);
      const stream = await transformApi.lumaScriptToArtifactDesign(lumascriptObj, project.suffix);

      let artifactDesignJson = '';
      for await (const chunk of readStream(stream)) {
        artifactDesignJson += chunk;
      }

      // Validate JSON before storing
      const artifactDesign = JSON.parse(artifactDesignJson);
      const normalizedDesign = normalizeArtifactDesign(artifactDesign, project.suffix ?? artifactDesign.projectSuffix ?? '');

      // Convert back to JSON string
      const normalizedJson = JSON.stringify(normalizedDesign);

      updateProjectStage(activeProjectId, 'ARTIFACT_DESIGN', normalizedJson, false);

      // Force tab switch even if already on ARTIFACT_DESIGN stage
      // This handles the case where user manually clicked away from the tab via GoldenLayout UI
      const currentStage = useProjectStore.getState().ui.activeStage;
      if (currentStage === 'ARTIFACT_DESIGN') {
        // Temporarily switch to a different stage, then back to force the effect to run
        setActiveStage('LUMASCRIPT');
        setTimeout(() => setActiveStage('ARTIFACT_DESIGN'), 0);
      } else {
        setActiveStage('ARTIFACT_DESIGN');
      }

      addToast('success', 'Artifact analysis complete');
    } catch (error: any) {
      console.error('Artifact design analysis error:', error);
      addToast('error', `Failed to analyze: ${error.message || 'Unknown error'}`);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    executeToArtifactDesign();
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  const handleZoomFit = () => {
    // TODO: Implement zoom-to-fit functionality
    // This requires access to ReactFlow's internal viewport controls
    console.log('Zoom to fit requested');
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Conversion Buttons Bar */}
      <div className="h-8 border-b border-border bg-background flex items-center justify-center px-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          {analyzing ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Analyzing...</span>
            </div>
          ) : (
            <>
              <button
                onClick={handleToBPMNPreview}
                disabled={!lumascript || analyzing}
                className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                To BPMN Preview
                <ArrowLeft className="w-3 h-3 rotate-180" />
              </button>
              <button
                onClick={handleToArtifactDesignClick}
                disabled={!lumascript || analyzing}
                className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                To Artifact Generation
                <ArrowLeft className="w-3 h-3 rotate-180" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* ReactFlow Canvas */}
      <div ref={containerRef} className="flex-1 relative" style={{ minHeight: 0, width: '100%', height: '100%' }}>
        {!lumascript ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <p className="text-sm mb-2">No LumaScript yet</p>
              <p className="text-xs">Generate LumaScript to see the visual flow</p>
            </div>
          </div>
        ) : parseError ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-red-600">
              <p className="text-sm mb-2">Failed to parse LumaScript</p>
              <p className="text-xs">{parseError}</p>
            </div>
          </div>
        ) : isReady ? (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.1}
            maxZoom={2}
            defaultEdgeOptions={{
              animated: false,
              style: { strokeWidth: 2 }
            }}
            style={{ width: '100%', height: '100%' }}
          >
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
            <Controls />
            <MiniMap
              nodeStrokeWidth={3}
              zoomable
              pannable
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.05)'
              }}
            />
          </ReactFlow>
        ) : null}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirm}
        title="Overwrite Existing Artifact Design?"
        message="This will overwrite the existing artifact design content. Are you sure?"
        confirmLabel="Overwrite"
        cancelLabel="Cancel"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
};

// Wrapper component with ReactFlowProvider
export const LumaScriptPreviewPanel: React.FC = () => {
  return (
    <ReactFlowProvider>
      <LumaScriptPreviewContent />
    </ReactFlowProvider>
  );
};
